import { getLineCoordinateSystem } from '@editorUtils'
import { projectPointOntoLineInXY, signedDistanceInDirectionOf } from '@geometry'
import { v4 as uuid } from 'uuid'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

export const MIN_LINE_ELEMENT_SPLIT_LENGTH = 0.1

type XYZ = { x: number; y: number; z: number }

export interface ProjectedSplitPoint {
  projected: ImmutableVector3
  distanceFromStart: number
  lineLength: number
}

export interface SplitLineElementData<T extends ShapeObjectLine = ShapeObjectLine> {
  updatedElement: T
  createdElement: T
}

const toVector = (point: XYZ): ImmutableVector3 => new ImmutableVector3(point.x, point.y, point.z)

export const projectSplitPointOntoLine = (
  start: XYZ,
  end: XYZ,
  splitPoint: XYZ,
  projectInXY: boolean,
): ProjectedSplitPoint => {
  const startVec = toVector(start)
  const endVec = toVector(end)
  const splitVec = toVector(splitPoint)
  const lineLength = startVec.distanceTo(endVec)

  if (projectInXY) {
    const projectedInXY = projectPointOntoLineInXY(start, end, splitPoint)
    if (projectedInXY) {
      return {
        projected: projectedInXY.projected,
        distanceFromStart: projectedInXY.t * projectedInXY.lineLength,
        lineLength: projectedInXY.lineLength,
      }
    }
  }

  const direction = endVec.sub(startVec).normalize()
  const distanceFromStart = signedDistanceInDirectionOf(startVec, splitVec, direction)
  const projected = startVec.add(direction.multiplyScalar(distanceFromStart))

  return {
    projected,
    distanceFromStart,
    lineLength,
  }
}

export const isValidLineElementSplit = (
  start: XYZ,
  end: XYZ,
  splitPoint: XYZ,
  projectInXY: boolean,
): boolean => {
  const { distanceFromStart, lineLength } = projectSplitPointOntoLine(
    start,
    end,
    splitPoint,
    projectInXY,
  )

  return (
    distanceFromStart >= MIN_LINE_ELEMENT_SPLIT_LENGTH &&
    distanceFromStart <= lineLength - MIN_LINE_ELEMENT_SPLIT_LENGTH
  )
}

export const splitLineElement = <T extends ShapeObjectLine>(
  element: T,
  splitPoint: XYZ,
  projectInXY: boolean,
): SplitLineElementData<T> => {
  const { start, end } = element.shape
  const { projected } = projectSplitPointOntoLine(start, end, splitPoint, projectInXY)
  const startVec = toVector(start)
  const endVec = toVector(end)

  const updatedElement: T = {
    ...element,
    is_local: undefined,
    is_updated: undefined,
    is_translated: undefined,
    domains: undefined,
    shape: {
      start: startVec,
      end: projected,
    },
    coordinate_system: getLineCoordinateSystem(startVec, projected),
  }

  const createdElement: T = {
    ...element,
    guid: uuid(),
    is_local: undefined,
    is_updated: undefined,
    is_translated: undefined,
    domains: undefined,
    shape: {
      start: projected,
      end: endVec,
    },
    coordinate_system: getLineCoordinateSystem(projected, endVec),
  }
  delete createdElement.label

  return {
    updatedElement,
    createdElement,
  }
}
